import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Trash2, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "How much should I save before getting my own authority?",
  "How do I know if a load is worth taking?",
  "What's the difference between factoring and a line of credit?",
  "How do I avoid failing my new entrant safety audit?",
];

export default function AICoach() {
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const endRef = useRef(null);
  const pendingAsk = useRef(location.state?.ask || null);

  useEffect(() => {
    if (location.state?.ask) navigate(location.pathname, { replace: true, state: {} });
  }, []); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/coach/history`).then(({ data }) => {
      setMessages(data);
      setLoadingHistory(false);
    }).catch(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/coach/chat`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The coach is unavailable right now.");
      setMessages((m) => m.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    try {
      await axios.delete(`${API}/coach/history`);
      setMessages([]);
    } catch {}
  };

  useEffect(() => {
    if (!loadingHistory && pendingAsk.current) {
      const q = pendingAsk.current;
      pendingAsk.current = null;
      send(q);
    }
  }, [loadingHistory]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SiteHeader />
      <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-8 flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-1">Paid tool</div>
            <h1 className="font-heading text-3xl tracking-tighter font-black text-slate-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-brand" /> AI Coach
            </h1>
          </div>
          {messages.length > 0 && (
            <button onClick={clear} data-testid="coach-clear" className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-900">
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          )}
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-md p-5 overflow-y-auto min-h-[52vh]" data-testid="coach-messages">
          {loadingHistory ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <div className="w-14 h-14 rounded-md bg-slate-900 flex items-center justify-center mb-5">
                <Truck className="w-7 h-7 text-brand" />
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Ask me anything about your trucking business.</h2>
              <p className="text-slate-500 mt-2 max-w-md">Authority, compliance, rates, cash flow, load boards — I'm your 24/7 mentor.</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-6 w-full max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="coach-suggestion" className="text-left text-sm text-slate-700 border border-slate-200 rounded-md p-3 hover:border-brand hover:bg-orange-50/40 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`coach-msg-${m.role}`}
                >
                  <div className={`max-w-[85%] rounded-md px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-brand text-white whitespace-pre-wrap" : "bg-slate-100 text-slate-800"
                  }`}>
                    {m.role === "user" ? m.content : (
                      <div className="coach-md space-y-2">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="coach-typing">
                  <div className="bg-slate-100 text-slate-500 rounded-md px-4 py-3 text-sm inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Coach is thinking…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="mt-4 flex items-end gap-3 bg-white border border-slate-200 rounded-md p-2"
        >
          <textarea
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask your coach anything…"
            rows={1}
            className="flex-1 resize-none bg-transparent px-3 py-2 text-sm text-slate-800 focus:outline-none max-h-32"
          />
          <Button type="submit" data-testid="coach-send" disabled={sending || !input.trim()} className="rounded-full bg-brand hover:bg-brand-hover text-white h-11 w-11 p-0 shrink-0 disabled:opacity-40">
            <Send className="w-4 h-4" />
          </Button>
        </form>
        <p className="text-[11px] text-slate-400 mt-2 text-center">AI guidance for education only — verify legal/financial specifics with FMCSA or a professional.</p>
      </div>
    </div>
  );
}
